import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize, switchMap } from 'rxjs';

import { AuthModalService } from '../../../../shared/services/auth-modal.service';
import { AuthService } from '../../../../shared/services/auth.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { InputComponent } from '../../../../shared/ui/input/input.component';

const GENERIC_ERROR = 'Ha ocurrido un error. Por favor, inténtalo de nuevo.';
const INVALID_CREDENTIALS = 'El correo o la contraseña no coinciden.';

@Component({
  selector: 'app-login-form',
  imports: [ReactiveFormsModule, InputComponent, ButtonComponent],
  templateUrl: './login-form.component.html',
  styleUrl: './login-form.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginFormComponent {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly authModalService = inject(AuthModalService);
  private readonly toastService = inject(ToastService);

  protected readonly loading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);

  protected readonly form = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]],
  });

  protected onSubmit(): void {
    if (this.form.invalid || this.loading()) {
      this.form.markAllAsTouched();
      return;
    }

    const { email, password } = this.form.getRawValue();
    this.loading.set(true);
    this.errorMessage.set(null);

    this.authService
      .login(email, password)
      .pipe(
        switchMap(() => this.authService.me()),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: (user) => {
          const name = user?.name ?? '';
          this.authModalService.close();
          this.toastService.show({
            type: 'success',
            message: `¡Hola de nuevo, ${name}!`,
          });
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 400 || err.status === 401) {
            this.errorMessage.set(INVALID_CREDENTIALS);
          } else {
            this.errorMessage.set(GENERIC_ERROR);
          }
        },
      });
  }

  protected switchToForgot(): void {
    this.authModalService.switchMode('forgot');
  }

  protected switchToRegister(): void {
    this.authModalService.switchMode('register');
  }
}
